// ── Scene Viewport (L0 metrics cache) ────────────────────────────────────
// Single owner of the #scene element's layout metrics. The rest of the
// codebase must read scene dimensions through this module instead of
// calling getBoundingClientRect() directly, so reads happen once per
// resize instead of every rAF frame.
//
// Invalidation:
//   • ResizeObserver on #scene  (catches CSS/layout-driven size changes)
//   • window 'resize'           (catches viewport changes)
//   • window 'orientationchange' (catches mobile rotations)
//
// Used by runtime/init.ts (mousemove camera normalization) and by any
// future code that needs to convert between client and scene pixels.

import { dom } from '../dom';

let cachedRect: DOMRect | null = null;
let measuring = false;
let initialized = false;

function measure(): DOMRect {
  measuring = true;
  try {
    return dom.scene.getBoundingClientRect();
  } finally {
    measuring = false;
  }
}

function invalidate(): void {
  cachedRect = null;
}

export function getSceneRect(): DOMRect {
  if (!cachedRect) cachedRect = measure();
  return cachedRect;
}

export function isMeasuringSceneRect(): boolean {
  return measuring;
}

export function initSceneViewport(): void {
  if (initialized) return;
  initialized = true;
  cachedRect = measure();
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(invalidate);
    ro.observe(dom.scene);
  }
  window.addEventListener('resize', invalidate, { passive: true });
  window.addEventListener('orientationchange', invalidate, { passive: true });
}
